/**
 * Gemeinsame Helfer für Tab „Medien & Dokumente" + Dashboard-Widgets.
 */
import React from 'react';
import type { TFunction } from 'i18next';
import { BRAND, BRAND_LIGHT } from '../../../../lib/theme';
import { fileExtension, type MediaDocItem, type MediaDocKind } from '../../../../lib/mediaDocuments';
import { formatDate } from '../../../../lib/dateFormat';

/** Anzeigename einer Kategorie; unbekannte (Alt-)Slugs erscheinen kapitalisiert. */
export function categoryLabel(tr: TFunction, kind: MediaDocKind, slug: string, role: 'pb' | 'bk' = 'pb'): string {
  const fallback = slug ? slug.charAt(0).toUpperCase() + slug.slice(1) : '—';
  if (kind === 'media') {
    if (slug === 'video' && role === 'bk') return tr('pflege.media.media_group.video_bk', { defaultValue: fallback });
    return tr(`pflege.media.media_group.${slug}`, { defaultValue: fallback });
  }
  return tr(`pflege.media.doc_group.${slug}`, { defaultValue: fallback });
}

/** „26.10.2026“ — einheitlich deutsch; `_lang` bleibt nur für bestehende Aufrufer. */
export function formatMediaDate(iso: string | null, _lang?: string): string {
  return formatDate(iso, '');
}

/** Kann im Browser direkt angezeigt werden (Lightbox)? */
export function isPreviewable(item: MediaDocItem): boolean {
  if (!item.url || item.processing) return false;
  if (item.is_pdf || item.is_video) return true;
  // HEIC/HEIF zeigt nur Safari an — in der Lightbox nicht anbieten.
  return item.is_image && !/heic|heif/i.test(item.mime);
}

const BADGE_TONES: Record<string, { bg: string; fg: string }> = {
  pdf:  { bg: '#fef3f2', fg: '#b42318' },
  doc:  { bg: '#eff8ff', fg: '#175cd3' },
  docx: { bg: '#eff8ff', fg: '#175cd3' },
  odt:  { bg: '#eff8ff', fg: '#175cd3' },
  txt:  { bg: '#f2f4f7', fg: '#475467' },
  xls:  { bg: '#ecfdf3', fg: '#067647' },
  xlsx: { bg: '#ecfdf3', fg: '#067647' },
  ods:  { bg: '#ecfdf3', fg: '#067647' },
  csv:  { bg: '#ecfdf3', fg: '#067647' },
};

/** Farbiges Dateityp-Kürzel (PDF, DOCX, …). */
export function FileBadge({ name, size = 36 }: { name: string; size?: number }) {
  const ext = fileExtension(name) || 'file';
  const tone = BADGE_TONES[ext] ?? { bg: BRAND_LIGHT, fg: BRAND };
  return (
    <span
      aria-hidden
      style={{
        width: size, height: size, borderRadius: 8, flexShrink: 0,
        background: tone.bg, color: tone.fg,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontSize: ext.length > 3 ? 9.5 : 10.5, fontWeight: 700, letterSpacing: '0.02em',
        textTransform: 'uppercase',
      }}
    >{ext.slice(0, 4)}</span>
  );
}

/** Kleines Dreieck-Overlay für Videos. */
export function PlayBadge({ size = 34 }: { size?: number }) {
  return (
    <span
      aria-hidden
      style={{
        position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)',
        width: size, height: size, borderRadius: '50%',
        background: 'rgba(16, 24, 40, 0.62)', backdropFilter: 'blur(2px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        pointerEvents: 'none',
      }}
    >
      <svg width={size * 0.42} height={size * 0.42} viewBox="0 0 20 20" fill="#fff"><path d="M6 3.8v12.4a.8.8 0 0 0 1.2.7l10-6.2a.8.8 0 0 0 0-1.4l-10-6.2A.8.8 0 0 0 6 3.8z" /></svg>
    </span>
  );
}

export function Spinner({ size = 18, color = BRAND }: { size?: number; color?: string }) {
  return (
    <span
      role="progressbar"
      aria-busy="true"
      style={{
        width: size, height: size, borderRadius: '50%', display: 'inline-block',
        border: `2px solid ${color}33`, borderTopColor: color,
        animation: 'sh24-media-spin 0.8s linear infinite',
      }}
    >
      <style>{'@keyframes sh24-media-spin { to { transform: rotate(360deg); } }'}</style>
    </span>
  );
}
